import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

// Only 14 (the legacy antd-inherited default baked into already-saved
// content) strips as "no styling". 20 — the standard base the editor now
// displays — is deliberately NOT stripped: an explicit author choice is
// preserved (review R3-001), and it stays harmless because the reader's
// px->em pivot is 20, so font-size:20px renders as 1em = base everywhere.
export const DEFAULT_EDITOR_FONT_SIZES_PX = [14];

const HTML_TAG_PATTERN = /<[a-z][\s\S]*>/i;
// Anchored to span open tags: draftjs-to-html escapes < and > in body text
// but not quotes, so prose can contain a literal ` style="..."` while a
// literal `<span ` cannot — only real tags match.
const SPAN_STYLE_TAG_PATTERN = /<span style="([^"]*)">/g;
const DEFAULT_FONT_SIZE_DECL_PATTERN = new RegExp(
  `^font-size:\\s*(?:${DEFAULT_EDITOR_FONT_SIZES_PX.join("|")})px$`,
  "i",
);
const BARE_SPAN_PATTERN = /<span>([\s\S]*?)<\/span>/g;

function stripDefaultFontSize(html: string): string {
  const stripped = html.replace(SPAN_STYLE_TAG_PATTERN, (tag, decls: string) => {
    const parsed = decls
      .split(";")
      .map((decl) => decl.trim())
      .filter(Boolean);
    const kept = parsed.filter(
      (decl) => !DEFAULT_FONT_SIZE_DECL_PATTERN.test(decl),
    );
    if (kept.length === parsed.length) return tag;
    if (kept.length === 0) return "<span>";
    return `<span style="${kept.join(";")};">`;
  });
  // html-to-draftjs drops attribute-less spans, so unwrapping them keeps the
  // stripped output stable under re-import/export.
  return stripped.replace(BARE_SPAN_PATTERN, "$1");
}

export function hasHtmlTags(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

export function preEscapeLegacyPlainText(raw: string): string {
  if (!raw || hasHtmlTags(raw)) return raw;
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

export function htmlToEditorState(value: string): EditorState {
  if (!value) return EditorState.createEmpty();
  const escaped = preEscapeLegacyPlainText(value);
  const blocks = htmlToDraft(escaped);
  if (!blocks) return EditorState.createEmpty();
  const content = ContentState.createFromBlockArray(
    blocks.contentBlocks,
    blocks.entityMap,
  );
  return EditorState.createWithContent(content);
}

export function editorStateToHtml(state: EditorState): string {
  const raw = convertToRaw(state.getCurrentContent());
  const html = stripDefaultFontSize(draftToHtml(raw));
  return html.trim() === "<p></p>" ? "" : html;
}
